import { i18n } from "@/i18n";

import {
  softwareIsNotExpired,
  isAuthenticated,
  hasValidLicense,
  loadWorkspaceModelInfo,
  preloadPublication,
  preloadModel,
  guardEditorArea,
  loadSharedModelInfo,
  preloadShare,
  forceOpenWorkspaceFolder,
  preloadOrganization,
  loadOrganizationMembers,
  loadTeams,
  hasJoinedWorkspace,
  preloadTeam,
  preloadPersonalWorkspace,
  loadWorkspaces,
  isUserAdmin,
  inModelGuard
} from "./navigationGuards.js";

import EmptyRouterView from "@core/routes/EmptyRouterView.vue";

// Editor areas
import EditorView from "@editor/EditorView.vue";
import Model from "@editor/views/model/ModelView.vue";
import Variables from "@editor/views/variables/Variables.vue";
import Inspector from "@editor/views/inspector/Inspector.vue";
import Simulation from "@editor/views/simulation/SimulationView.vue";
import EvaluationView from "@editor/views/evaluation/EvaluationView.vue";

// pages
import ImprintPage from "@manager/pages/ImprintPage.vue";
import NoPassword from "@manager/pages/NoPassword.vue";
import SoftwareExpired from "@manager/pages/SoftwareExpired.vue";

// Error pages
import ErrorOrganizationNotFound from "@manager/organizations/ErrorOrganizationNotFound.vue";
import ErrorNotFoundView from "@manager/pages/ErrorNotFoundView.vue";

// Manager (Model manager)

// Auth
import PreAuthViewLayout from "@manager/common/PreAuthViewLayout.vue";
import AuthView from "@manager/auth/AuthView.vue";
import SetPasswordView from "@manager/auth/SetPasswordView.vue";
import MagicLoginView from "@manager/auth/MagicLoginView.vue";

import SynchroSettingsView from "@manager/settings/SynchroSettingsView.vue";
import ManagerView from "@manager/ManagerView.vue";

// onboarding
import SetNameView from "@manager/onboarding/SetNameView.vue";
import ChooseLicenseView from "@manager/onboarding/ChooseLicenseView.vue";
import OnboardingPasswordView from "@manager/onboarding/OnboardingPasswordView.vue";
import OnboardingSynchronizationView from "@manager/onboarding/OnboardingSynchronizationView.vue";

// Payment
import CheckoutCanceledView from "@manager/purchasing/CheckoutCanceledView.vue";
import CheckoutSuccessView from "@manager/purchasing/CheckoutSuccessView.vue";

// workspaces
import WorkspaceView from "@manager/workspaces/WorkspaceView.vue";
import WorkspaceFolderView from "@manager/workspaces/WorkspaceFolderView.vue";
import WorkspaceModelView from "@manager/workspacemodels/WorkspaceModelView.vue";
import WorkspaceEmptyView from "@manager/workspaces/WorkspaceEmptyView.vue";

// Shared
import SharedModelsView from "@manager/sharedModels/SharedModelsView.vue";
import SharedModelView from "@manager/sharedModels/SharedModelView.vue";

// Public
import PublicModelsView from "@manager/publicModels/PublicModelsView.vue";
import PublicModelView from "@manager/publicModels/PublicModelView.vue";

// Account settings
import SettingsView from "@manager/settings/SettingsView.vue";
import AccountSettingsView from "@manager/settings/AccountSettingsView.vue";
import ProfileSettingsView from "@manager/settings/ProfileSettingsView.vue";
import LicenseOrderingView from "@manager/purchasing/LicenseOrderingView.vue";

// New Organization Views
import OrganizationSettingsView from "@manager/organizations/OrganizationSettingsView.vue";
import OrganizationJoinView from "@manager/organizations/OrganizationJoinView.vue";
import TeamSettingsView from "@manager/teams/TeamSettingsView.vue";
import TeamJoinView from "@manager/teams/TeamJoinView.vue";
import SubscriptionDetailsView from "@manager/subscriptions/SubscriptionDetailsView.vue";
import SubscriptionModifyView from "@manager/subscriptions/SubscriptionModifyView.vue";

// splash screen
import Splash from "@/app/Splash.vue";

// admin
import AdminView from "@manager/admin/AdminView.vue";
import AdminHomeView from "@manager/admin/AdminHomeView.vue";
import AdminUserView from "@manager/admin/AdminUserView.vue";
import AdminUsersListView from "@manager/admin/AdminUsersListView.vue";
import AdminUserCreateView from "@manager/admin/AdminUserCreateView.vue";
import AdminUserSubscriptionEditView from "@manager/admin/AdminUserSubscriptionEditView.vue";
import AdminUserSubscriptionCreateView from "@manager/admin/AdminUserSubscriptionCreateView.vue";
import AdminOrganizationsListView from "@manager/admin/AdminOrganizationsListView.vue";
import AdminOrganizationDetailView from "@manager/admin/AdminOrganizationDetailView.vue";
import AdminSubscriptionRequestCreateView from "@manager/admin/AdminSubscriptionRequestCreateView.vue";

import { useLoadedModel } from "@editor/store/loadedModel.store";
import { useWorkspaces } from "@/api/workspaces.store";
import { useModels } from "@/api/models.store";
import { useProjectStore } from "@editor/store/project.store";
import { useUser } from "@/api/user.store.js";

import type { RouteRecordRaw } from "vue-router";
import type { SimcisionRouteMeta } from "./routeTypes";

const editorChildren = [
  {
    path: "model",
    component: Model,
    meta: {
      areaName: "model",
      isEditorView: true
    }
  },
  {
    path: "variables",
    component: Variables,
    meta: {
      areaName: "variables",
      isEditorView: true
    }
  },
  {
    path: "inspector",
    component: Inspector,
    meta: {
      areaName: "inspector",
      isEditorView: true
    }
  },
  {
    path: "simulation",
    component: Simulation,
    meta: {
      areaName: "simulation",
      isEditorView: true
    }
  },
  {
    path: "evaluation",
    component: EvaluationView,
    meta: {
      areaName: "evaluation",
      isEditorView: true
    }
  }
  /*{
    path: "",
    redirect: (to) => {
      // send the user to the model path by default.
      const pathParts = to.fullPath.split("/");
      if (pathParts[pathParts.length - 1] == to.params.id) {
        return { path: pathParts.concat("model").join("/") };
      } else {
        return {
          path: pathParts.slice(0, -1).concat("model").join("/")
        };
      }
    }
  }*/
];

type ExtendedRoute = RouteRecordRaw & {
  meta?: SimcisionRouteMeta;
  children?: ExtendedRoute[];
};

export const routes: ExtendedRoute[] = [
  {
    path: "/",
    alias: "",
    component: Splash,
    name: "splash",
    meta: {
      guards: [softwareIsNotExpired]
    }
  },

  // AUTH and registering
  {
    path: "",
    component: PreAuthViewLayout,
    meta: {
      guards: [softwareIsNotExpired]
    },
    children: [
      // AUTH
      {
        path: "login",
        component: AuthView,
        name: "login",
        props: { form: "login" }
      },
      {
        path: "register",
        component: AuthView,
        name: "register",
        props: { form: "register", step: "register" }
      },
      {
        path: "register/confirm",
        component: AuthView,
        name: "register-confirm",
        props: { form: "register", step: "confirm-email" }
      },
      {
        path: "register/password",
        component: AuthView,
        name: "register-password",
        props: { form: "register", step: "password" }
      },
      {
        path: "reset-password",
        component: AuthView,
        name: "reset-password",
        props: { form: "reset-password" }
      },
      {
        path: "set-password/:uid/:token",
        component: SetPasswordView,
        name: "set-password",
        props: true
      },
      {
        path: "magic-login/:uid/:token/:type?/:relatedPk?",
        component: MagicLoginView,
        name: "magic-login",
        props: true
      }
    ]
  },

  {
    path: "",
    component: ManagerView,
    meta: {
      guards: [isAuthenticated]
    },
    children: [
      {
        path: "/organizations/:organizationSlug/join",
        component: OrganizationJoinView,
        name: "organization-join",
        meta: {
          guards: [preloadOrganization]
        },
        props: true
      },
      {
        path: "/teams/:organizationSlug/:teamSlug/join",
        component: TeamJoinView,
        name: "team-join",
        props: true,
        meta: {
          guards: [preloadTeam]
        }
      },
      {
        path: "subscriptions/order",
        component: LicenseOrderingView,
        name: "order-subscription"
      },
      {
        path: "checkout/cancel",
        component: CheckoutCanceledView,
        name: "checkout-canceled"
      },
      { 
        path: "checkout/success",
        component: CheckoutSuccessView,
        name: "checkout-success",
      }
    ]
  },

  {
    path: "/onboarding",
    component: ManagerView,
    meta: {
      guards: [isAuthenticated]
    },
    children: [
      {
        path: "name",
        name: "onboarding-name",
        component: SetNameView
      },
      {
        path: "license",
        name: "onboarding-license",
        component: ChooseLicenseView
      },
      {
        path: "password",
        name: "onboarding-password",
        component: OnboardingPasswordView
      },
      {
        path: "synchronization",
        name: "onboarding-synchronization",
        component: OnboardingSynchronizationView
      }
    ]
  },

  // === MANAGER ===

  // MODELS
  {
    path: "/models",
    component: ManagerView,
    meta: {
      guards: [
        softwareIsNotExpired,
        isAuthenticated,
        loadWorkspaces
      ]
    },
    children: [
      {
        path: "",
        name: "models-explorer",
        redirect: to => {
          const { user } = useUser();
          let lastOpenFolder = localStorage.getItem(`u${user.value?.id}_lastOpenFolder`);
          if (lastOpenFolder) {
            return {
              path: lastOpenFolder
            }
          }
          return {
            path: "/models/my"
          }
        }
      },
      {
        path: "my",
        component: WorkspaceView,
        props: true,
        meta: {
          guards: [preloadPersonalWorkspace]
        },
        children: [
          {
            path: "",
            component: WorkspaceEmptyView,
            props: true,
            name: "personal-workspace",
            meta: {
              guards: [forceOpenWorkspaceFolder]
            }
          },
          {
            path: ":folderSlug",
            component: EmptyRouterView,
            meta: {
              guards: [{
                name: "saveFolderPath",
                update(to) {
                  const { user } = useUser();
                  const folderPath = `/models/my/${to.params.folderSlug}`;
                  localStorage.setItem(`u${user.value?.id}_lastOpenFolder`, folderPath);
                }
              }]
            },
            children: [
              {
                path: "",
                name: "personal-workspace-folder",
                component: WorkspaceFolderView,
                props: true,
                meta: {
                  title(to) {
                    const { currentFolder } = useWorkspaces(to.params);
                    return currentFolder.value?.name;
                  }
                }
              },
              {
                path: ":modelId",
                name: "personal-workspace-model-info",
                component: WorkspaceModelView,
                props: true,
                meta: {
                  guards: [loadWorkspaceModelInfo],
                  async title(to) {
                    const { waitForModel } = useModels(to.params);
                    const model = await waitForModel(
                      to.params.modelId as string
                    );
                    return model?.name;
                  }
                }
              }
            ]
          }
        ]
      },
      {
        // SHARED MODELS
        path: "shared",
        component: WorkspaceView,
        props: true,
        meta: {
          guards: [
            preloadPersonalWorkspace
          ]
        },
        children: [
          {
            path: "",
            component: SharedModelsView,
            name: "shared-models",
            meta: {
              title() {
                return i18n.t("labels.sharedModels");
              }
            }
          },
          {
            path: ":shareId",
            name: "shared-model-info",
            component: SharedModelView,
            props: (route) => ({
              ...route.params,
              shareId: parseInt(route.params.shareId as string)
            }),
            meta: {
              guards: [loadSharedModelInfo]
            }
          }
        ]
      },
      {
        // EXAMPLE MODELS
        path: "public",
        component: WorkspaceView,
        props: true,
        meta: {
          guards: [preloadPersonalWorkspace]
        },
        children: [
          {
            path: "",
            component: PublicModelsView,
            name: "public-models",
            meta: {
              title() {
                return i18n.t("labels.exampleModels");
              }
            }
          },
          {
            path: ":publicationId",
            name: "public-model-info",
            component: PublicModelView,
            props: true
          }
        ]
      },
      {
        path: ":organizationSlug/:teamSlug",
        component: WorkspaceView,
        props: true,
        meta: {
          guards: [
            preloadTeam,
            hasJoinedWorkspace
          ]
        },
        children: [
          {
            path: "",
            component: WorkspaceEmptyView,
            props: true,
            name: "team",
            meta: {
              guards: [forceOpenWorkspaceFolder]
            }
          },
          {
            path: ":folderSlug",
            component: EmptyRouterView,
            meta: {
              guards: [{
                name: "saveFolderPath",
                update(to) {
                  const { user } = useUser();
                  const folderPath = `/models/${to.params.organizationSlug}/${to.params.teamSlug}/${to.params.folderSlug}`;
                  localStorage.setItem(`u${user.value?.id}_lastOpenFolder`, folderPath);
                }
              }]
            },
            children: [
              {
                path: "",
                component: WorkspaceFolderView,
                props: true,
                name: "team-folder",
                meta: {
                  title(to) {
                    const { currentFolder } = useWorkspaces(to.params);
                    return currentFolder.value?.name;
                  }
                }
              },
              {
                path: ":modelId",
                name: "team-model-info",
                component: WorkspaceModelView,
                props: true,
                meta: {
                  guards: [loadWorkspaceModelInfo],
                  async title(to) {
                    const { waitForModel } = useModels(to.params);
                    const model = await waitForModel(
                      to.params.modelId as string
                    );
                    return model?.name;
                  }
                }
              }
            ]
          }
        ]
      }
    ]
  },

  {
    // EDITOR group
    // Edit a specific model
    // Makes sure model is loaded and user has access to it
    // This is the actual EDITING view.
    path: "/models/public/:publicationId/view",
    component: EditorView,
    meta: {
      guards: [
        softwareIsNotExpired,
        preloadPublication,
        guardEditorArea,
        inModelGuard
      ],
      title() {
        const { loadedModel } = useLoadedModel();
        const internalModelTitle = useProjectStore().title;
        return internalModelTitle || loadedModel.name;
      }
    },
    children: [...editorChildren],
    name: "public-model"
    // end of model group
  },

  {
    // EDITOR group
    // Edit a specific model
    // Makes sure model is loaded and user has access to it
    // This is the actual EDITING view.
    path: "/models/shared/:shareId/view",
    component: EditorView,
    meta: {
      guards: [
        softwareIsNotExpired,
        isAuthenticated,
        preloadShare,
        guardEditorArea,
        inModelGuard
      ],
      title() {
        const { loadedModel } = useLoadedModel();
        const internalModelTitle = useProjectStore().title;
        return internalModelTitle || loadedModel.name;
      }
    },
    children: [...editorChildren],
    name: "shared-model"
    // end of model group
  },

  {
    // EDITOR group
    // Edit a specific model
    // Makes sure model is loaded and user has access to it
    // This is the actual EDITING view.
    path: "/models/my/:folderSlug/:modelId/view",
    component: EditorView,
    meta: {
      guards: [
        softwareIsNotExpired,
        isAuthenticated,
        preloadPersonalWorkspace,
        preloadModel,
        guardEditorArea,
        inModelGuard
      ],
      title() {
        const { loadedModel } = useLoadedModel();
        const internalModelTitle = useProjectStore().title;
        return internalModelTitle || loadedModel.name;
      }
    },
    children: [...editorChildren],
    name: "personal-workspace-model"
  },
  {
    // EDITOR group
    // Edit a specific model
    // Makes sure model is loaded and user has access to it
    // This is the actual EDITING view.
    path: "/models/:organizationSlug/:teamSlug/:folderSlug/:modelId/view",
    component: EditorView,
    meta: {
      guards: [
        softwareIsNotExpired,
        isAuthenticated,
        hasValidLicense,
        preloadTeam,
        hasJoinedWorkspace,
        preloadModel,
        guardEditorArea,
        inModelGuard
      ],
      title() {
        const { loadedModel } = useLoadedModel();
        const internalModelTitle = useProjectStore().title;
        return internalModelTitle || loadedModel.name;
      }
    },
    children: [...editorChildren],
    name: "team-model"
  },
  {
    // Pages can be accessed by anyone, anytime.
    // This content is NOT secured at all.
    path: "/page",
    name: "page",
    component: EmptyRouterView,
    children: [
      {
        path: "imprint",
        name: "imprint",
        component: ImprintPage
      }
    ]
  },
  {
    path: "/error",
    component: EmptyRouterView,
    children: [
      {
        path: "software-expired",
        name: "softwareExpired",
        component: SoftwareExpired,
        props: true
      },
      {
        path: "no-password",
        name: "noPassword",
        component: NoPassword,
        props: true
      }
    ]
  },

  // SETTINGS
  {
    path: "/settings",
    component: SettingsView,
    meta: {
      guards: [softwareIsNotExpired, isAuthenticated, loadWorkspaces]
    },
    children: [
      {
        path: "subscriptions",
        component: AccountSettingsView,
        name: "settings-subscriptions"
      },
      {
        path: "license/update",
        redirect: "subscriptions/order"
      },
      {
        path: "subscriptions/:subscriptionId",
        component: SubscriptionDetailsView,
        name: "subscription-details",
        props: (route) => {
          return {
            subscriptionId: parseInt(route.params.subscriptionId as string)
          }
        },
      },
      {
        path: "subscriptions/:subscriptionId/modify",
        component: SubscriptionModifyView,
        name: "subscription-modify",
        props: (route) => {
          return {
            subscriptionId: parseInt(route.params.subscriptionId as string)
          }
        },
      },
      {
        path: "subscriptions/:subscriptionId/upgrade",
        component: SubscriptionModifyView,
        name: "subscription-upgrade",
        props: (route) => {
          return {
            subscriptionId: parseInt(route.params.subscriptionId as string),
            mode: "upgrade"
          }
        },
      },
      {
        path: "subscriptions/:subscriptionId/downgrade",
        component: SubscriptionModifyView,
        name: "subscription-downgrade",
        props: (route) => {
          return {
            subscriptionId: parseInt(route.params.subscriptionId as string),
            mode: "downgrade"
          }
        },
      },
      {
        path: "profile",
        component: ProfileSettingsView,
        name: "settings-profile"
      },
      {
        path: "teams/:organizationSlug/:teamSlug",
        component: TeamSettingsView,
        name: "settings-team",
        props: true,
        meta: {
          guards: [preloadTeam, hasJoinedWorkspace]
        }
      },
      {
        path: "organizations/:organizationSlug",
        component: EmptyRouterView,
        props: true,
        children: [
          {
            path: "",
            name: "settings-organization",
            component: OrganizationSettingsView,
            props: true,
            meta: {
              guards: [preloadOrganization, loadOrganizationMembers, loadTeams]
            }
          },
          {
            path: "not-found",
            component: ErrorOrganizationNotFound,
            name: "error-organization-not-found",
            props: true
          }
        ]
      },
      {
        path: "synchronization",
        name: "settings-synchronization",
        component: SynchroSettingsView
      }
    ]
  },

  // ADMIN views
  {
    path: "/admin",
    component: ManagerView,
    meta: {
      guards: [isAuthenticated, isUserAdmin]
    },
    children: [
      {
        path: "",
        component: AdminView,
        props: true,
        children: [
          {
            path: "",
            name: "admin-home",
            component: AdminHomeView,
            props: true
          },
          {
            path: "users",
            name: "admin-users-list",
            component: AdminUsersListView,
            props: true
          },
          {
            path: "users/create",
            name: "admin-users-create",
            component: AdminUserCreateView,
            props: true
          },
          {
            path: "users/:userId",
            name: "admin-users-update",
            component: AdminUserView,
            props: true
          },
          {
            path: "users/:userId/subscription-create",
            name: "admin-subscription-create",
            component: AdminUserSubscriptionCreateView,
            props: true
          },
          {
            path: "users/:userId/subscription-requests/new",
            name: "admin-subscription-request-create",
            component: AdminSubscriptionRequestCreateView,
            props: true
          },
          {
            path: "users/:userId/subscriptions/:subscriptionId",
            name: "admin-user-update-subscription",
            component: AdminUserSubscriptionEditView,
            props: true
          },
          {
            path: "organizations",
            name: "admin-organizations-list",
            component: AdminOrganizationsListView,
            props: true
          },
          {
            path: "organizations/:organizationId",
            name: "admin-organizations-detail",
            component: AdminOrganizationDetailView,
            props: true
          }
        ]
      }
    ]
  },

  // REDIRECT short publication links to the full editor view
  {
    path: "/p/:publicationId/:areaCode*",
    redirect: (to) => {
      const areaCodes: Record<string, string> = {
        "m": "model",
        "v": "variables",
        "i": "inspector",
        "s": "simulation",
        "e": "evaluation"
      }
      const areaName = areaCodes[to.params.areaCode as string];
      if (areaName) {
        return {
          path: `/models/public/${to.params.publicationId}/view/${areaName}`
        };
      }
      return {
        path: `/models/public/${to.params.publicationId}/view`
      };
    }
  },

  // REDIRECT all old routes to new routes (preventing 404 errors)
  {
    path: "/view/publications/:publicationId/:areaName*",
    redirect: (to) => {
      console.log("to with areaName", to.params.areaName);
      if (to.params.areaName) {
        return {
          path: `/models/public/${to.params.publicationId}/view/${to.params.areaName}`
        };
      }
      return {
        path: `/models/public/${to.params.publicationId}/view`
      };
    }
  },
  {
    path: "/view/models/:modelId/:areaName*",
    redirect: (to) => {
      return {
        name: "personal-workspace-model",
        params: to.params
      };
    }
  },
  {
    path: "/models/update/:modelId",
    redirect: () => {
      return {
        name: "personal-workspace"
      };
    }
  },
  {
    path: "/user",
    redirect: "settings-profile"
  },

  {
    path: "/empty",
    name: "Empty",
    component: EmptyRouterView
  },

  // will match everything and put it under `$route.params.pathMatch`
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: ErrorNotFoundView,
    beforeEnter: (to) => {
      console.log("not found", to);
    }
  }
];
